import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout/Layout';
import Spinner from '../components/ui/Spinner';
import { adminApi } from '../config/axios';
import { useGlobal } from '../contexts/GlobalContext';
import { useDocumentTitle } from '../config/utils';
import ProtectionsPanel from '../components/ProtectionsPanel';
import SupportContactCard from '../components/SupportContactCard';
import OpsAlertsCard from '../components/OpsAlertsCard';

const TIMEZONES = [
  'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi',
  'America/Chicago', 'America/Los_Angeles', 'America/New_York', 'America/Sao_Paulo',
  'America/Toronto',
  'Asia/Amman', 'Asia/Baghdad', 'Asia/Bahrain', 'Asia/Bangkok', 'Asia/Beirut',
  'Asia/Colombo', 'Asia/Dubai', 'Asia/Jakarta', 'Asia/Jerusalem', 'Asia/Karachi',
  'Asia/Kolkata', 'Asia/Kuala_Lumpur', 'Asia/Kuwait', 'Asia/Manila', 'Asia/Muscat',
  'Asia/Nicosia', 'Asia/Qatar', 'Asia/Riyadh', 'Asia/Singapore', 'Asia/Tehran',
  'Asia/Tokyo',
  'Atlantic/Reykjavik',
  'Australia/Melbourne', 'Australia/Sydney',
  'Europe/Amsterdam', 'Europe/Athens', 'Europe/Berlin', 'Europe/Brussels',
  'Europe/Budapest', 'Europe/Istanbul', 'Europe/Lisbon', 'Europe/London',
  'Europe/Madrid', 'Europe/Moscow', 'Europe/Paris', 'Europe/Rome',
  'Pacific/Auckland', 'Pacific/Honolulu',
  'UTC',
];

export default function Settings() {
  useDocumentTitle('Settings');
  const { showToast } = useGlobal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm] = useState({ city: '', country: '', timezone: 'Asia/Riyadh' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    adminApi.get('/settings/prayer-defaults')
      .then(res => {
        const d = res.data?.data ?? {};
        setForm({
          city:     d.city     ?? '',
          country:  d.country  ?? '',
          timezone: d.timezone ?? 'Asia/Riyadh',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.city.trim())    e.city    = 'City is required.';
    if (!form.country.trim()) e.country = 'Country is required.';
    if (!form.timezone)       e.timezone = 'Timezone is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await adminApi.post('/settings/prayer-defaults', form);
      showToast('success', 'Saved', 'Prayer location defaults updated.');
    } catch (err) {
      const msg = err?.response?.data?.message ?? 'Failed to save settings.';
      showToast('error', 'Error', msg);
    } finally {
      setSaving(false);
    }
  };

  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  if (loading) return <Layout><div className="flex items-center justify-center py-16"><Spinner size="lg" /></div></Layout>;

  return (
    <Layout>
      <div className="page-header mb-6">
        <div>
          <h1 className="section-title text-xl">Settings</h1>
          <p className="text-text-muted text-sm mt-0.5">Platform-wide configuration</p>
        </div>
      </div>

      {/* Default Prayer Location */}
      <div className="card-padded mb-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-text-main">Default Prayer Location</h2>
            <p className="text-text-muted text-sm mt-0.5">
              Fallback location used for prayer time calculations when a device has no location configured.
            </p>
          </div>
          <button onClick={save} disabled={saving} className="btn-primary flex-shrink-0">
            {saving ? <Spinner size="sm" /> : 'Save'}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* City */}
          <div>
            <label className="label">City</label>
            <input
              type="text"
              value={form.city}
              onChange={e => set('city', e.target.value)}
              placeholder="e.g. Riyadh"
              className={`input-base ${errors.city ? 'border-danger/60 focus:border-danger/60' : ''}`}
            />
            {errors.city && <p className="text-danger text-xs mt-1">{errors.city}</p>}
          </div>

          {/* Country */}
          <div>
            <label className="label">Country</label>
            <input
              type="text"
              value={form.country}
              onChange={e => set('country', e.target.value)}
              placeholder="e.g. Saudi Arabia"
              className={`input-base ${errors.country ? 'border-danger/60 focus:border-danger/60' : ''}`}
            />
            {errors.country && <p className="text-danger text-xs mt-1">{errors.country}</p>}
          </div>

          {/* Timezone */}
          <div>
            <label className="label">Timezone</label>
            <select
              value={form.timezone}
              onChange={e => set('timezone', e.target.value)}
              className={`input-base ${errors.timezone ? 'border-danger/60 focus:border-danger/60' : ''}`}
            >
              {TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            {errors.timezone && <p className="text-danger text-xs mt-1">{errors.timezone}</p>}
          </div>
        </div>
      </div>

      {/* Ops alerts (internal): summary recipients + Telegram status */}
      <div className="mb-6"><OpsAlertsCard /></div>

      {/* Customer support contact (WhatsApp) — global default */}
      <div className="mb-6"><SupportContactCard /></div>

      {/* Protections — fleet defaults (global scope) */}
      <div className="card-padded mb-6">
        <h2 className="text-base font-semibold text-text-main">Protections — fleet defaults</h2>
        <p className="text-text-muted text-sm mt-0.5">
          Global defaults for the self-healing protections and the HDMI-IN audio delay. A company or device override wins over these.
        </p>
        <p className="text-gold text-xs mt-2 mb-3">Global changes apply to every device within about 60 seconds.</p>
        <ProtectionsPanel scope="global" />
      </div>
    </Layout>
  );
}
